import axios from 'axios';
import { getToken } from './authService';

// Backend API URL
const API_URL = process.env.REACT_APP_API_URL || 'https://wealthplayrepo.onrender.com/api';

// Interface for transaction data
export interface Transaction {
  id: string;
  plaidId?: string;
  name?: string;
  merchant_name?: string;
  amount: number;
  date: string;
  category: string[];
  budget_category?: string; // Need, Want, or Savings
  pending: boolean;
  account_id?: string;
  isManual?: boolean;
}

// Interface for account data
export interface Account {
  account_id: string;
  balances: {
    available: number;
    current: number;
    limit: number | null;
  };
  name: string;
  type: string;
  subtype: string | null;
}

class PlaidService {
  private linkToken: string | null = null;

  async createLinkToken(): Promise<string> {
    try {
      console.log('Creating Plaid link token...');
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/plaid/create_link_token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const linkToken = response.data.link_token;
      if (!linkToken) {
        throw new Error('No link token received from server');
      }
      this.linkToken = linkToken;
      console.log('Plaid link token created successfully');
      return linkToken;
    } catch (error) {
      console.error('Error creating Plaid link token:', error);
      if (axios.isAxiosError(error)) {
        console.error('API Error:', error.response?.data);
        throw new Error(error.response?.data?.details || 'Failed to create Plaid link token. Please try again.');
      }
      throw error;
    }
  }

  async exchangePublicToken(publicToken: string): Promise<void> {
    try {
      console.log('Exchanging public token for access token...');
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/plaid/exchange-token`,
        { public_token: publicToken },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Public token exchanged successfully');
      return response.data;
    } catch (error) {
      console.error('Error exchanging public token:', error);
      if (axios.isAxiosError(error)) {
        console.error('API Error:', error.response?.data);
        throw new Error(error.response?.data?.details || 'Failed to exchange public token. Please try again.');
      }
      throw error;
    }
  }

  async fetchTransactions(retryCount = 0): Promise<Transaction[]> {
    try {
      console.log('Fetching transactions...');
      const token = getToken();
      const response = await axios.get(
        `${API_URL}/plaid/fetch-transactions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Transactions fetched successfully:', {
        count: response.data.transactions?.length || 0,
        total: response.data.total_transactions,
        request_id: response.data.request_id
      });
      return response.data.transactions || [];
    } catch (error) {
      console.error('Error fetching transactions:', error);
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.details || error.response?.data?.error || 'Failed to fetch transactions';
        console.error('API Error:', error.response?.data);
        
        // Check if this is the initial sync delay error
        if (errorMessage.includes('not yet ready') && retryCount < 3) {
          const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
          console.log(`Initial sync not ready. Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.fetchTransactions(retryCount + 1);
        }
        
        throw new Error(errorMessage);
      }
      throw error;
    }
  }

  async getPersistedTransactions(): Promise<Transaction[]> {
    try {
      console.log('Fetching persisted transactions...');
      const token = getToken();
      const response = await axios.get(
        `${API_URL}/plaid/transactions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Persisted transactions fetched successfully');
      return response.data.transactions || [];
    } catch (error) {
      console.error('Error fetching persisted transactions:', error);
      if (axios.isAxiosError(error)) {
        console.error('API Error:', error.response?.data);
        throw new Error(error.response?.data?.details || 'Failed to fetch persisted transactions. Please try again.');
      }
      throw error;
    }
  }

  async fetchAccounts(): Promise<Account[]> {
    try {
      const token = getToken();
      const response = await axios.get(
        `${API_URL}/plaid/accounts`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.accounts || [];
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        console.warn('Plaid accounts endpoint not found (404). Returning empty accounts list.');
        return [];
      }
      console.error('Error fetching accounts:', error);
      throw new Error('Failed to fetch accounts');
    }
  }

  async resetTransactions(): Promise<{ success: boolean; message: string; count: number }> {
    try {
      console.log('Resetting transactions...');
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/plaid/reset-transactions`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Transactions reset successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error resetting transactions:', error);
      if (axios.isAxiosError(error)) {
        console.error('API Error:', error.response?.data);
        throw new Error(error.response?.data?.details || 'Failed to reset transactions. Please try again.');
      }
      throw error;
    }
  }

  async updateTransactionCategories(categories: Array<{ plaidId: string; budget_category: string }>): Promise<{ success: boolean; message: string; updatedCount: number; errors?: string[] }> {
    try {
      console.log('PlaidService: Updating transaction categories...');
      console.log('PlaidService: Categories being sent:', categories);
      console.log('PlaidService: Savings categories in request:', categories.filter(c => c.budget_category === 'Savings'));
      
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/plaid/update-transaction-categories`,
        { categories },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('PlaidService: Transaction categories updated successfully:', response.data);
      console.log('PlaidService: Updated count from server:', response.data.updatedCount);
      console.log('PlaidService: Any errors from server:', response.data.errors);
      
      return response.data;
    } catch (error) {
      console.error('PlaidService: Error updating transaction categories:', error);
      if (axios.isAxiosError(error)) {
        console.error('PlaidService: API Error details:', error.response?.data);
        throw new Error(error.response?.data?.details || 'Failed to update transaction categories. Please try again.');
      }
      throw error;
    }
  }

  async disconnectAccount(): Promise<{ success: boolean; message: string }> {
    try {
      console.log('Disconnecting Plaid account...');
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/plaid/disconnect`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Plaid account disconnected successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error disconnecting Plaid account:', error);
      if (axios.isAxiosError(error)) {
        console.error('API Error:', error.response?.data);
        throw new Error(error.response?.data?.error || 'Failed to disconnect bank account. Please try again.');
      }
      throw error;
    }
  }

  getLinkToken(): string | null {
    return this.linkToken;
  }
}

const plaidServiceInstance = new PlaidService();

export default plaidServiceInstance;

// Function to get accounts
export const getAccounts = async (accessToken: string): Promise<Account[]> => {
  try {
    console.log('Fetching accounts...');
    const response = await axios.post('/api/accounts', {
      access_token: accessToken
    });
    
    console.log('Accounts fetched successfully');
    return response.data.accounts;
  } catch (error) {
    console.error('Error fetching accounts:', error);
    if (axios.isAxiosError(error)) {
      console.error('API Error:', error.response?.data);
    }
    throw new Error('Failed to fetch accounts. Please try again.');
  }
}; 